import { TEN_BILLION } from "@finway-group/shared/lib/consts"
import { type Employee } from "@finway-group/shared/lib/models"
import { isDateOfBirthInPast } from "@finway-group/shared/lib/utils/date.utils"
import { textNoWhiteSpacesRegex } from "@finway-group/shared/lib/utils/validators"
import { type Rule } from "antd/lib/form"
import { type FormInstance } from "antd/lib/form/Form"
import { type Moment } from "moment"
import { isValidPhoneNumber } from "react-phone-number-input"
import { parseCurrencyInput } from "~Components/currencyInput/config"
import { i18n } from "~Shared/locales/i18n"
import { countInArray, isNumeric, isValidBIC, isValidIBAN } from "~Shared/utils/helper.utils"

export const dateOfBirthRules: Array<Rule> = [
    {
        validator: (_rule, value?: Moment) => (!value || isDateOfBirthInPast(value) ? Promise.resolve() : Promise.reject(new Error(i18n.t("validation:date_of_birth_in_future")))),
    },
]

export const createRequiredDateOfBirthRules = (): Array<Rule> => [{ required: true, message: i18n.t("validation:date_of_birth_missing") }, ...dateOfBirthRules]

export const createEmployeeRules = (possibleApprovers: Array<Employee>, formInstance: FormInstance, fallbackApproverId?: string, employee?: Employee) => ({
    workPhone: [
        {
            validator: (_rule: Rule, value: string) => {
                if (value && !isValidPhoneNumber(value) && value !== "") {
                    return Promise.reject(i18n.t("validation:phone"))
                }
                if (employee?.companyProfiles?.some((companyProfile) => companyProfile?.weavrData?.userId) && !value) {
                    return Promise.reject(i18n.t("validation:work_phone_deletion_not_allowed"))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    team: [
        {
            required: true,
            message: i18n.t("validation:required"),
        },
    ] as Array<Rule>,
    creditorNumber: [
        {
            validator: (_rule, value: string) => {
                // DATEV limit
                if (value) {
                    if (!isNumeric(value)) {
                        return Promise.reject(i18n.t("validation:numeric"))
                    }

                    if (value.length > 0 && value.substr(0, 1) === "0") {
                        return Promise.reject(i18n.t("validation:no_leading_zero"))
                    }

                    // DATEV limit
                    if (value.length < 5) {
                        return Promise.reject(i18n.t("validation:string_too_short"))
                    }
                    if (value.length > 9) {
                        return Promise.reject(i18n.t("validation:string_too_long"))
                    }

                    if (!textNoWhiteSpacesRegex.test(value)) {
                        return Promise.reject(new Error(i18n.t("validation:string_white_space")))
                    }
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    limit: [
        {
            validator: (_rule, inputValue: string) => {
                let value = 0
                inputValue = inputValue ? inputValue.toString() : ""
                if (inputValue !== "") {
                    value = parseCurrencyInput(inputValue)
                    if (value <= 0) {
                        return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: 0 })))
                    }
                    if (
                        countInArray(
                            formInstance.getFieldValue("approvals").map((approval: any) => parseCurrencyInput(approval.limit)),
                            value,
                        ) >= 2
                    ) {
                        return Promise.reject(i18n.t("validation:duplicate_approval_limit"))
                    }
                    return Promise.resolve()
                }

                return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: 0 })))
            },
        },
    ] as Array<Rule>,
    approver: [
        {
            validator: (_rule: Rule, value: string) => {
                if (!value || value === "") {
                    return Promise.reject(new Error(i18n.t("validation:required")))
                }

                if (!possibleApprovers.map((approver: Employee) => approver.id).includes(value)) {
                    return Promise.reject(new Error(i18n.t("validation:limit_approver")))
                }

                if (
                    countInArray(
                        formInstance.getFieldValue("approvals").map((approval: any) => approval.approver),
                        value,
                    ) >= 2
                ) {
                    return Promise.reject(i18n.t("validation:duplicate_limit_approver"))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    mileageRate: [
        {
            validator: (_rule: Rule, value: string) => {
                const number = Number(value)
                if (value && isNaN(number)) {
                    return Promise.reject(i18n.t("validation:number"))
                }
                if (number < 0 || number > TEN_BILLION) {
                    return Promise.reject(i18n.t("validation:numeric_min_max", { min: 0, max: "10B" }))
                }
                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    iban: [
        {
            validator: (_rule, value: string) => {
                if (value && value !== "" && !isValidIBAN(value)) {
                    return Promise.reject(new Error(i18n.t("validation:iban")))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    bic: [
        {
            validator: (_rule, value: string) => {
                if (value && value !== "" && !isValidBIC(value)) {
                    return Promise.reject(new Error(i18n.t("validation:bic")))
                }

                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
    dateOfBirth: dateOfBirthRules,
})
